import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Tag,
  Typography,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  ExpenseCategory,
  getExpenseCategories,
  createExpenseCategory,
  updateExpenseCategory,
  deleteExpenseCategory,
} from '../../services/expenseService';

const { Title } = Typography;

const CATEGORY_ICONS = [
  '🍽️', '🚗', '🛒', '📄', '🎮', '🏥', '📚', '💡', '🏠', '👕', '🎬', '✈️', '💊', '🎨', '🏋️', '📱'
];

const CATEGORY_COLORS = [
  '#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#6B7280',
  '#84CC16', '#F97316', '#6366F1', '#14B8A6', '#F43F5E', '#A855F7', '#EAB308', '#22C55E'
];

export const ExpenseCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ExpenseCategory | undefined>();
  const [form] = Form.useForm();

  const loadCategories = async () => {
    setLoading(true);
    try {
      const categoriesData = await getExpenseCategories();
      setCategories(categoriesData);
    } catch (error) {
      message.error('Không thể tải danh mục');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleAddCategory = () => {
    setEditingCategory(undefined);
    form.resetFields();
    form.setFieldsValue({
      icon: '💰',
      color: '#3B82F6',
    });
    setIsModalOpen(true);
  };

  const handleEditCategory = (category: ExpenseCategory) => {
    setEditingCategory(category);
    form.setFieldsValue({
      name: category.name,
      icon: category.icon,
      color: category.color,
    });
    setIsModalOpen(true);
  };

  const handleDeleteCategory = async (id: number) => {
    try {
      await deleteExpenseCategory(id);
      message.success('Xóa danh mục thành công');
      loadCategories();
    } catch (error) {
      message.error('Có lỗi xảy ra khi xóa danh mục');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingCategory) {
        await updateExpenseCategory(editingCategory.id, values);
        message.success('Cập nhật danh mục thành công');
      } else {
        await createExpenseCategory(values);
        message.success('Thêm danh mục thành công');
      }

      setIsModalOpen(false);
      loadCategories();
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Vui lòng nhập đầy đủ thông tin');
      } else {
        message.error('Có lỗi xảy ra khi lưu danh mục');
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'icon',
      render: (icon: string) => <span style={{ fontSize: '20px' }}>{icon}</span>,
    },
    {
      title: 'Tên danh mục',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Màu sắc',
      dataIndex: 'color',
      key: 'color',
      render: (color: string) => (
        <div
          style={{
            width: 20,
            height: 20,
            backgroundColor: color,
            borderRadius: '50%',
            display: 'inline-block',
          }}
        />
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Hoạt động' : 'Không hoạt động'}
        </Tag>
      ),
    },
    {
      title: 'Mặc định',
      dataIndex: 'is_default',
      key: 'is_default',
      render: (isDefault: boolean) => (
        <Tag color={isDefault ? 'blue' : 'default'}>
          {isDefault ? 'Có' : 'Không'}
        </Tag>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: ExpenseCategory) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditCategory(record)}
          />
          {!record.is_default && (
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa danh mục này?"
              onConfirm={() => handleDeleteCategory(record.id)}
              okText="Có"
              cancelText="Không"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const activeCategories = categories.filter(cat => cat.is_active);
  const defaultCategories = categories.filter(cat => cat.is_default);

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Title level={3}>Quản lý Danh mục Chi tiêu</Title>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Tổng danh mục"
              value={categories.length}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Danh mục hoạt động"
              value={activeCategories.length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Danh mục mặc định"
              value={defaultCategories.length}
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCategory}
              style={{ width: '100%', height: '100%' }}
            >
              Thêm danh mục
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title={editingCategory ? 'Chỉnh sửa danh mục' : 'Thêm danh mục mới'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleSubmit}
          >
            {editingCategory ? 'Cập nhật' : 'Thêm'}
          </Button>,
        ]}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            icon: '💰',
            color: '#3B82F6',
          }}
        >
          <Form.Item
            name="name"
            label="Tên danh mục"
            rules={[{ required: true, message: 'Vui lòng nhập tên danh mục' }]}
          >
            <Input placeholder="Nhập tên danh mục" />
          </Form.Item>

          <Form.Item
            name="icon"
            label="Icon"
          >
            <Select placeholder="Chọn icon">
              {CATEGORY_ICONS.map((icon) => (
                <Select.Option key={icon} value={icon}>
                  {icon}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="color"
            label="Màu sắc"
          >
            <Select placeholder="Chọn màu sắc">
              {CATEGORY_COLORS.map((color) => (
                <Select.Option key={color} value={color}>
                  <Space>
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        backgroundColor: color,
                        borderRadius: '50%',
                        display: 'inline-block',
                      }}
                    />
                    <span>{color}</span>
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};